<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import {
  computed, nextTick, ref, watch,
} from 'vue';

// eslint-disable-next-line import/order
import type { Batteries } from '~/types/equipment';

import ModalForm from '~/components/shared/ModalForm.vue';
import SearchableSelect from '~/components/shared/SearchableSelect.vue';
import TagInput from '~/components/shared/TagInput.vue';
import {
  EQUIPMENT_LABELS,
  FORM_HELP,
  FORM_PLACEHOLDERS,
  getSkusDuplicateMessage,
  MODAL_TITLES,
} from '~/constants/equipment';
import { useBrandsOptions } from '~/shared/hooks/useBrands';

import {
  useBatteryById, useCreateBattery, useUpdateBattery,
} from '../queries/useBatteriesCrud';
import { type BatteryFormData, batterySchema } from '../schemas/batterySchema';

export interface BatteryModalProps {
  batteryData?: Batteries.Battery | null;
  batteryId?: null | number;
  mode: 'create' | 'edit' | 'view';
  modelValue: boolean;
}

const props = withDefaults(defineProps<BatteryModalProps>(), {
  batteryData: null,
  batteryId: null,
});

const emit = defineEmits<{
  'success': [];
  'update:modelValue': [value: boolean];
}>();

const selectedBatteryId = ref<null | number>(props.batteryId);
const modalFormRef = ref();

const createMutation = useCreateBattery();
const updateMutation = useUpdateBattery();

const { isLoading: isLoadingBrands, options: brandsOptions } = useBrandsOptions('batteries');

const shouldFetchById = computed(() => (props.mode === 'edit' || props.mode === 'view') && !!selectedBatteryId.value);

const { data: fetchedBatteryData } = useBatteryById(
  computed(() => (shouldFetchById.value ? selectedBatteryId.value : null)),
);

const {
  defineField,
  errors,
  handleSubmit,
  resetForm,
  setFieldError,
  setValues,
} = useForm<BatteryFormData>({
  initialValues: {
    brand: '',
    is_enabled: true,
    model: '',
    power: 0,
    skus: null,
  },
  validateOnMount: false,
  validationSchema: toTypedSchema(batterySchema),
});

const fieldOptions = {
  validateOnBlur: false,
  validateOnChange: false,
  validateOnInput: false,
  validateOnModelUpdate: false,
};

const [brand] = defineField('brand', fieldOptions);
const [model, modelProps] = defineField('model', fieldOptions);
const [power, powerProps] = defineField('power', fieldOptions);
const [skus] = defineField('skus', fieldOptions);
const [isEnabled, isEnabledProps] = defineField('is_enabled', fieldOptions);

const isVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const modalTitle = computed(() => {
  switch (props.mode) {
    case 'create':
      return MODAL_TITLES.BATTERY.CREATE;
    case 'edit':
      return MODAL_TITLES.BATTERY.EDIT;
    case 'view':
      return MODAL_TITLES.BATTERY.VIEW;
    default:
      return 'Bateria';
  }
});

const modalIsLoading = computed(() => createMutation.isPending.value || updateMutation.isPending.value);

watch(() => props.batteryId, (newId) => {
  selectedBatteryId.value = newId;
});

watch(() => props.batteryData, (data) => {
  if (data && (props.mode === 'edit' || props.mode === 'view')) {
    nextTick(() => {
      setValues({
        brand: data.brand || '',
        is_enabled: data.is_enabled,
        model: data.model || '',
        power: data.power || 0,
        skus: data.skus?.length ? data.skus : null,
      });
    });
  }
}, { immediate: true });

watch(() => fetchedBatteryData.value, (freshData) => {
  if (freshData && (props.mode === 'edit' || props.mode === 'view') && !props.batteryData) {
    nextTick(() => {
      setValues({
        brand: freshData.brand || '',
        is_enabled: freshData.is_enabled,
        model: freshData.model || '',
        power: freshData.power || 0,
        skus: freshData.skus?.length ? freshData.skus : null,
      });
    });
  }
}, { immediate: false });

watch(() => props.modelValue, (isOpen) => {
  if (!isOpen) {
    nextTick(() => {
      resetForm();
      selectedBatteryId.value = null;
    });
  } else {
    selectedBatteryId.value = props.batteryId;

    if (props.mode === 'create') {
      nextTick(() => {
        resetForm();
        setValues({
          brand: '',
          is_enabled: true,
          model: '',
          power: 0,
        });
      });
    } else if (props.mode === 'edit' || props.mode === 'view') {
      if (props.batteryData) {
        const data = props.batteryData;
        nextTick(() => {
          setValues({
            brand: data.brand || '',
            is_enabled: data.is_enabled,
            model: data.model || '',
            power: data.power || 0,
            skus: data.skus?.length ? data.skus : null,
          });
        });
      }
    }
  }
}, { immediate: false });

// Clear field error functions
const clearBrandError = () => setFieldError('brand', undefined);
const clearModelError = () => setFieldError('model', undefined);
const clearPowerError = () => setFieldError('power', undefined);

// Validate field functions
const validateBrand = async () => {
  try {
    setFieldError('brand', undefined);
    const brandData = {
      brand: brand.value, is_enabled: true, model: '', power: 0.1,
    };
    await batterySchema.parseAsync(brandData);
  } catch (error: any) {
    const fieldError = error.errors?.find((err: any) => err.path[0] === 'brand');
    if (fieldError) setFieldError('brand', fieldError.message);
  }
};

const validateModel = async () => {
  try {
    setFieldError('model', undefined);
    const modelData = {
      brand: '', is_enabled: true, model: model.value, power: 0.1,
    };
    await batterySchema.parseAsync(modelData);
  } catch (error: any) {
    const fieldError = error.errors?.find((err: any) => err.path[0] === 'model');
    if (fieldError) setFieldError('model', fieldError.message);
  }
};

const validatePower = async () => {
  try {
    setFieldError('power', undefined);
    const powerData = {
      brand: '', is_enabled: true, model: '', power: power.value,
    };
    await batterySchema.parseAsync(powerData);
  } catch (error: any) {
    const fieldError = error.errors?.find((err: any) => err.path[0] === 'power');
    if (fieldError) setFieldError('power', fieldError.message);
  }
};

const handleSave = handleSubmit(async (values) => {
  try {
    const apiData = {
      brand: values.brand,
      model: values.model,
      power: values.power,
      skus: values.skus?.length ? values.skus : [],
      ...(props.mode !== 'create' && { is_enabled: values.is_enabled }),
    };

    if (props.mode === 'create') {
      const createData = {
        brand: values.brand,
        is_enabled: true,
        model: values.model,
        power: values.power,
        skus: values.skus?.length ? values.skus : [],
      };
      await createMutation.mutateAsync(createData);
    } else {
      const id = props.batteryData?.id || selectedBatteryId.value;
      if (id) {
        await updateMutation.mutateAsync({
          data: apiData,
          id,
        });
      }
    }

    emit('success');
    modalFormRef.value?.resetEditingState();
    isVisible.value = false;
  } catch (error) {
    // Error handled by mutation
  }
});

const handleCancel = () => {
  isVisible.value = false;
};

const handleEdit = () => {
  emit('update:modelValue', false);
  nextTick(() => {
    emit('update:modelValue', true);
  });
};

</script>

<template>
  <ModalForm
    ref="modalFormRef"
    v-model="isVisible"
    :title="modalTitle"
    :mode="mode"
    :loading="modalIsLoading"
    size="lg"
    @save="handleSave"
    @edit="handleEdit"
    @cancel="handleCancel"
  >
    <template
      #default="{
        isViewMode: modalIsViewMode, isLoading: modalIsLoading,
      }">
      <div class="row">
        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label required" for="brand">{{ EQUIPMENT_LABELS.BRAND }}</label>
            <SearchableSelect
              id="brand"
              v-model="brand"
              :options="brandsOptions"
              :loading="isLoadingBrands"
              :disabled="modalIsViewMode || modalIsLoading"
              :has-error="!!errors.brand"
              :error-message="errors.brand"
              :placeholder="FORM_PLACEHOLDERS.BRAND_SEARCH"
              loading-text="Carregando marcas..."
              no-options-text="Nenhuma marca encontrada"
              @input="clearBrandError"
              @blur="validateBrand"
            />
          </div>
        </div>

        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label required" for="model">{{ EQUIPMENT_LABELS.MODEL }}</label>
            <input
              id="model"
              v-model="model"
              v-bind="modelProps"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.model }"
              :disabled="modalIsViewMode || modalIsLoading"
              :placeholder="FORM_PLACEHOLDERS.MODEL"
              @input="clearModelError"
              @blur="validateModel"
            />
            <div v-if="errors.model" class="invalid-feedback">
              {{ errors.model }}
            </div>
          </div>
        </div>

        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label required" for="power">{{ EQUIPMENT_LABELS.POWER }}</label>
            <input
              id="power"
              v-model.number="power"
              v-bind="powerProps"
              type="number"
              class="form-control"
              :class="{ 'is-invalid': errors.power }"
              :disabled="modalIsViewMode || modalIsLoading"
              :placeholder="FORM_PLACEHOLDERS.POWER"
              min="0.1"
              max="999"
              step="0.1"
              @input="clearPowerError"
              @blur="validatePower"
            />
            <div v-if="errors.power" class="invalid-feedback">
              {{ errors.power }}
            </div>
          </div>
        </div>

        <div v-if="mode !== 'create'" class="col-md-6">
          <div class="mb-3">
            <label class="form-label required" for="isEnabled">{{ EQUIPMENT_LABELS.STATUS }}</label>
            <select
              id="isEnabled"
              v-model="isEnabled"
              v-bind="isEnabledProps"
              class="form-select"
              :class="{ 'is-invalid': errors.is_enabled }"
              :disabled="modalIsViewMode || modalIsLoading"
            >
              <option :value="true">Ativado</option>
              <option :value="false">Desativado</option>
            </select>
            <div v-if="errors.is_enabled" class="invalid-feedback">
              {{ errors.is_enabled }}
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <div class="mb-3">
            <TagInput
              id="skus"
              v-model="skus"
              :label="EQUIPMENT_LABELS.SKUS"
              :placeholder="FORM_PLACEHOLDERS.SKUS"
              :help-text="FORM_HELP.SKUS"
              item-label="SKU"
              :duplicate-message="getSkusDuplicateMessage"
              :disabled="modalIsViewMode || modalIsLoading"
              :error="errors.skus"
              :invalid="!!errors.skus"
            />
          </div>
        </div>
      </div>
    </template>
  </ModalForm>
</template>
